import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { brandAliasesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { Brand, BrandLine } from '@rock/types';
import { Plus, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

interface BrandLinesTabProps {
    brand: Brand;
    readOnly?: boolean;
    onAddLine: (displayName: string) => Promise<void>;
    onRemoveLine: (line: BrandLine) => Promise<void>;
}

/**
 * The brand's product lines — Brunswick, Profile, Infinity: the families a nameplate prints after the
 * brand ("Hunter Brunswick"). Each line's keys are brand-qualified alias docs that resolve to the brand
 * WITH the line, so a homeowner who types or picks the line sees it, while every gate keys on the brand.
 */
export const BrandLinesTab: FC<BrandLinesTabProps> = ({ brand, readOnly, onAddLine, onRemoveLine }) => {
    const aliasDocs = useAsyncAtomValue(
        useMemo(() => brandAliasesState(brand.id), [brand.id]),
        [],
    );
    const keysByLine = useMemo(() => {
        const byLine = new Map<string, string[]>();
        for (const doc of aliasDocs) {
            if (!doc.lineId) continue;
            byLine.set(doc.lineId, [...(byLine.get(doc.lineId) ?? []), doc.id]);
        }
        return byLine;
    }, [aliasDocs]);
    const [input, setInput] = useState('');
    const [isMutating, setIsMutating] = useState(false);
    const name = input.trim();
    const canAdd = !!name && !isMutating && !readOnly;

    const handleAdd = async () => {
        if (!canAdd) return;
        setIsMutating(true);
        try {
            await onAddLine(name);
            setInput('');
        } catch {
            // Toast handled by the page; keep the input so the admin can adjust.
        } finally {
            setIsMutating(false);
        }
    };

    const handleRemove = async (line: BrandLine) => {
        setIsMutating(true);
        try {
            await onRemoveLine(line);
        } finally {
            setIsMutating(false);
        }
    };

    const lines = brand.lines ?? [];
    return (
        <Card>
            <CardHeader>
                <CardTitle>Product lines</CardTitle>
                <CardDescription>
                    Families within the brand, shown as the nameplate reads them (“
                    {brand.displayName ?? brand.canonicalName} Brunswick”). Never a gate: recalls, coverage and manuals
                    key on the brand.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                {!readOnly && (
                    <div className="flex gap-2">
                        <Input
                            placeholder="Add a line (e.g. Brunswick)"
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && canAdd) void handleAdd();
                            }}
                            disabled={isMutating}
                        />
                        <Button onClick={handleAdd} disabled={!canAdd}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add
                        </Button>
                    </div>
                )}
                {lines.length === 0 ? (
                    <div className="section-description">No lines yet.</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Line</TableHead>
                                <TableHead>Resolvable keys</TableHead>
                                <TableHead className="w-[80px] text-right" aria-label="Actions" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {lines.map((line) => (
                                <TableRow key={line.id}>
                                    <TableCell className="font-medium">
                                        {line.displayName}{' '}
                                        <span className="font-mono text-xs text-muted-foreground">{line.id}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="flex flex-wrap gap-1">
                                            {(keysByLine.get(line.id) ?? []).sort().map((key) => (
                                                <Badge key={key} variant="outline" className="font-mono">
                                                    {key}
                                                </Badge>
                                            ))}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {!readOnly && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => void handleRemove(line)}
                                                disabled={isMutating}
                                                aria-label={`Remove line ${line.displayName}`}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
